import { Alert, Button } from '@components'
import { useBusinessContext } from '@context'
import { useDebouncedValue } from '@hooks'
import { fetchHeaderPath, showAccount, validateAccountNumber } from '@query'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect } from 'react'
import { Control, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { FaEye } from 'react-icons/fa'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components'

interface Props {
  control: Control
  onClose: () => void
  currentAccountId?: number
}

export const AccountPathVisualizer: React.FC<Props> = ({ control, currentAccountId, onClose }) => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const number = useWatch({ control, name: 'number' })
  const { debouncedValue: debouncedNumber, setValue: setDebouncedNumber } = useDebouncedValue({
    initialValue: number,
    delay: 300
  })
  useEffect(() => {
    setDebouncedNumber(number)
  }, [number])

  const isEnabled = !!debouncedNumber
  const history = useHistory()

  const { data } = useQuery({
    queryKey: [slug, 'header_path', debouncedNumber],
    queryFn: () => fetchHeaderPath({ slug, number: debouncedNumber }),
    ...{
      enabled: isEnabled,
      placeholderData: keepPreviousData
    }
  })

  const { data: validationData } = useQuery({
    queryKey: [slug, 'account_number_validation', debouncedNumber],
    queryFn: () => validateAccountNumber({ slug, number: debouncedNumber }),
    ...{
      enabled: isEnabled,
      placeholderData: keepPreviousData
    }
  })

  const accountData = validationData?.data
  const accountId = accountData?.id
  const accountName = accountData?.name

  const numberAlreadyExists = validationData?.exists
  const doesExist = validationData?.exists
  const headers = data?.headers ?? []
  const lastHeader = headers[headers.length - 1]

  const showMutation = useMutation({
    mutationFn: () => showAccount({ slug, accountId: validationData?.data.id }),
    ...{
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug, 'accounts'] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'tabular_accounts'] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'auto_complete_accounts'] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'account_number_validation'] })
        ])

        history.push(`/${slug}/accounts?id=${accountId}`)
        onClose()
      }
    }
  })

  const isAlreadyShown = accountData?.is_shown
  const isCurrentAccount = accountData?.id === currentAccountId

  const getAlertContent = () => {
    if (doesExist && !isCurrentAccount) {
      if (isAlreadyShown) {
        return (
          <StyledAccountPathVisualizer>
            <Trans i18nKey="accounts.accountNumberInUse" values={{ accountName }}>
              <AnimatedTitle>
                <Link to={`/${slug}/accounts?id=${accountId}`} />
              </AnimatedTitle>
            </Trans>
          </StyledAccountPathVisualizer>
        )
      } else {
        return (
          <StyledAccountPathVisualizer>
            <Trans i18nKey="accounts.accountNumberInUseHidden1" values={{ accountName }}>
              <AnimatedTitle>
                <Link to={`/${slug}/accounts?id=${accountId}`} />
              </AnimatedTitle>
            </Trans>
            <br />
            <br />
            <Trans i18nKey="accounts.accountNumberInUseHidden2" />
            <ButtonContainer>
              <Button
                icon={<FaEye />}
                type="button"
                intent="warning"
                onClick={() => {
                  toast.promise(showMutation.mutateAsync(), {
                    loading: t('accounts.unhideInProgress'),
                    success: t('accounts.unhideSuccess'),
                    error: t('general.error')
                  })
                }}
              >
                {t('accounts.unhideAccount')}
              </Button>
            </ButtonContainer>
          </StyledAccountPathVisualizer>
        )
      }
    }

    return (
      <StyledAccountPathVisualizer>
        {lastHeader ? (
          <span>{t('accounts.accountBelongsToCategory')} </span>
        ) : (
          <span>{t('accounts.selectAccountNumber')}</span>
        )}
        <AnimatePresence mode="wait">
          <AnimatedTitle key={lastHeader}>{lastHeader}</AnimatedTitle>
        </AnimatePresence>
      </StyledAccountPathVisualizer>
    )
  }

  return (
    <Alert
      type={numberAlreadyExists && !isCurrentAccount ? 'warning' : 'info'}
      isVisible={true}
      description={getAlertContent()}
    />
  )
}

const StyledAccountPathVisualizer = styled.div``

const AnimatedTitle = styled(motion.span).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
})`
  font-weight: bold;

  & > * {
    color: ${({ theme }) => theme.colors.neutralBlack} !important;
  }
`

const ButtonContainer = styled.div`
  margin-top: 0.4rem;
  width: 100%;
`
